import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { VendorApi } from '@mesa-labs/mesa-api/dist/apis/vendor';
import { types as api } from '@mesa-labs/mesa-api';

import { prepareHeaders, useApi } from './api';

const scopeKey = (scope: api.FaqScope): string => `${scope.partnerId}:${scope.programCode}`;

export const faqsApi = createApi({
  reducerPath: 'faqsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CONFIG.api.vendorUrl,
    prepareHeaders: async (headers) => await prepareHeaders(headers),
  }),
  tagTypes: ['FaqContent', 'FaqItemDefaults', 'FaqAudit'],
  endpoints: (build) => ({
    getFaqContent: build.query<readonly api.FaqSectionResponse[], api.FaqScope>({
      async queryFn(scope) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getFaqContent(scope);
        return { data };
      },
      providesTags: (result, error, scope) => [{ type: 'FaqContent' as const, id: scopeKey(scope) }],
    }),

    getFaqItemDefaults: build.query<readonly api.FaqItemDefaultResponse[], void>({
      async queryFn() {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getFaqItemDefaults();
        return { data };
      },
      providesTags: ['FaqItemDefaults'],
    }),

    // Where a default is used. Loaded when the Usage modal opens; shares the defaults tag, which every
    // mutation that adds, removes, links or overrides an item (or renames or removes its section) invalidates.
    getFaqItemDefaultUsages: build.query<readonly api.FaqItemDefaultUsageResponse[], string>({
      async queryFn(id) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getFaqItemDefaultUsages(id);
        return { data };
      },
      providesTags: ['FaqItemDefaults'],
    }),

    getFaqAudit: build.query<api.IPagedResults<api.FaqAuditEntryResponse>, api.FaqAuditQuery>({
      async queryFn(params) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.getFaqAudit(params);
        return { data };
      },
      providesTags: ['FaqAudit'],
    }),

    // A restored row can belong to any scope, so every scope's content is refreshed.
    restoreFaqSection: build.mutation<api.FaqSectionResponse, { id: string }>({
      async queryFn({ id }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.restoreFaqSection(id);
        return { data };
      },
      invalidatesTags: ['FaqContent', 'FaqItemDefaults', 'FaqAudit'],
    }),

    restoreFaqItem: build.mutation<api.FaqItemResponse, { id: string }>({
      async queryFn({ id }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.restoreFaqItem(id);
        return { data };
      },
      invalidatesTags: ['FaqContent', 'FaqItemDefaults', 'FaqAudit'],
    }),

    // A default's text (and how many items link to it) shows up in every scope that uses it, so
    // these two invalidate every scope's content rather than just the current one.
    patchFaqItemDefault: build.mutation<api.FaqItemDefaultResponse, api.PatchFaqItemDefaultRequest & { id: string }>({
      async queryFn({ id, ...body }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.patchFaqItemDefault(id, body);
        return { data };
      },
      invalidatesTags: ['FaqContent', 'FaqItemDefaults', 'FaqAudit'],
    }),

    makeFaqItemDefault: build.mutation<api.FaqItemResponse, { id: string }>({
      async queryFn({ id }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.makeFaqItemDefault(id);
        return { data };
      },
      invalidatesTags: ['FaqContent', 'FaqItemDefaults', 'FaqAudit'],
    }),

    createFaqSection: build.mutation<api.FaqSectionResponse, api.CreateFaqSectionRequest>({
      async queryFn(body) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.createFaqSection(body);
        return { data };
      },
      invalidatesTags: (result, error, body) => [{ type: 'FaqContent' as const, id: scopeKey(body) }, 'FaqItemDefaults', 'FaqAudit'],
    }),

    patchFaqSection: build.mutation<api.FaqSectionResponse, api.PatchFaqSectionRequest & { id: string; scope: api.FaqScope }>({
      async queryFn({
        id, scope, ...body
      }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.patchFaqSection(id, body);
        return { data };
      },
      invalidatesTags: (result, error, { scope }) => [{ type: 'FaqContent' as const, id: scopeKey(scope) }, 'FaqItemDefaults', 'FaqAudit'],
    }),

    deleteFaqSection: build.mutation<void, { id: string; scope: api.FaqScope }>({
      async queryFn({ id, scope }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.deleteFaqSection(id);
        return { data };
      },
      invalidatesTags: (result, error, { scope }) => [{ type: 'FaqContent' as const, id: scopeKey(scope) }, 'FaqItemDefaults', 'FaqAudit'],
    }),

    createFaqItem: build.mutation<api.FaqItemResponse, api.CreateFaqItemRequest & { sectionId: string; scope: api.FaqScope }>({
      async queryFn({
        sectionId, scope, ...body
      }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.createFaqItem(sectionId, body);
        return { data };
      },
      invalidatesTags: (result, error, { scope }) => [{ type: 'FaqContent' as const, id: scopeKey(scope) }, 'FaqItemDefaults', 'FaqAudit'],
    }),

    patchFaqItem: build.mutation<api.FaqItemResponse, api.PatchFaqItemRequest & { id: string; scope: api.FaqScope }>({
      async queryFn({
        id, scope, ...body
      }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.patchFaqItem(id, body);
        return { data };
      },
      invalidatesTags: (result, error, { scope }) => [{ type: 'FaqContent' as const, id: scopeKey(scope) }, 'FaqItemDefaults', 'FaqAudit'],
    }),

    deleteFaqItem: build.mutation<void, { id: string; scope: api.FaqScope }>({
      async queryFn({ id, scope }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.deleteFaqItem(id);
        return { data };
      },
      invalidatesTags: (result, error, { scope }) => [{ type: 'FaqContent' as const, id: scopeKey(scope) }, 'FaqItemDefaults', 'FaqAudit'],
    }),

    copyFaqScope: build.mutation<void, api.CopyFaqScopeRequest>({
      async queryFn({ from, to }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.copyFaqScope({ from, to });
        return { data };
      },
      invalidatesTags: (result, error, { to }) => [{ type: 'FaqContent' as const, id: scopeKey(to) }, 'FaqItemDefaults', 'FaqAudit'],
    }),

    // Reorder mutations patch the getFaqContent cache immediately (rather than only invalidating
    // it, like every other mutation above) so a second drag/arrow move can start right away
    // instead of computing from a stale order while the first request is still in flight. On
    // failure the patch is undone and the scope is invalidated, in case the failure was someone
    // else's concurrent edit rather than a transient network error.
    reorderFaqSections: build.mutation<readonly api.FaqSectionOrderResponse[], api.ReorderFaqSectionsRequest>({
      async queryFn(body) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.reorderFaqSections(body);
        return { data };
      },
      async onQueryStarted({ sectionIds, ...scope }, { dispatch, queryFulfilled }) {
        // Returning a new array from the recipe (rather than mutating draft) replaces the cached
        // value outright -- sidesteps the readonly array type the recipe otherwise sees.
        const patchResult = dispatch(faqsApi.util.updateQueryData('getFaqContent', scope, (draft) => {
          const byId = new Map(draft.map((section) => [section.id, section]));
          return sectionIds.map((id) => byId.get(id)).filter((section): section is api.FaqSectionResponse => !!section);
        }));

        try {
          const { data } = await queryFulfilled;
          // Reconciles sortOrder with what the server actually committed -- the optimistic patch
          // above only reordered the array, so this is the self-healing path if the two ever diverge.
          dispatch(faqsApi.util.updateQueryData('getFaqContent', scope, (draft) => {
            const confirmedById = new Map(data.map((section) => [section.id, section]));
            return draft.map((section) => {
              const confirmed = confirmedById.get(section.id);
              return confirmed ? { ...section, sortOrder: confirmed.sortOrder } : section;
            });
          }));
        } catch {
          patchResult.undo();
          dispatch(faqsApi.util.invalidateTags([{ type: 'FaqContent' as const, id: scopeKey(scope) }]));
        }
      },
    }),

    reorderFaqItems: build.mutation<readonly api.FaqItemResponse[], api.ReorderFaqItemsRequest & { sectionId: string; scope: api.FaqScope }>({
      async queryFn({
        sectionId, scope, ...body
      }) {
        const vendorApi = useApi((options) => new VendorApi(CONFIG.api.vendorUrl, options));
        const data = await vendorApi.reorderFaqItems(sectionId, body);
        return { data };
      },
      async onQueryStarted({ sectionId, scope, itemIds }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(faqsApi.util.updateQueryData('getFaqContent', scope, (draft) => draft.map((section) => {
          if (section.id !== sectionId) {
            return section;
          }
          const byId = new Map(section.items.map((item) => [item.id, item]));
          const reordered = itemIds.map((id) => byId.get(id)).filter((item): item is api.FaqItemResponse => !!item);
          return { ...section, items: reordered };
        })));

        try {
          const { data } = await queryFulfilled;
          // Reconciles sortOrder with what the server actually committed -- the optimistic patch
          // above only reordered the array, so this is the self-healing path if the two ever diverge.
          dispatch(faqsApi.util.updateQueryData('getFaqContent', scope, (draft) => draft.map((section) => {
            if (section.id !== sectionId) {
              return section;
            }
            const confirmedById = new Map(data.map((item) => [item.id, item]));
            return {
              ...section,
              items: section.items.map((item) => {
                const confirmed = confirmedById.get(item.id);
                return confirmed ? { ...item, sortOrder: confirmed.sortOrder } : item;
              }),
            };
          })));
        } catch {
          patchResult.undo();
          dispatch(faqsApi.util.invalidateTags([{ type: 'FaqContent' as const, id: scopeKey(scope) }]));
        }
      },
    }),
  }),
});

export const {
  useGetFaqContentQuery,
  useLazyGetFaqContentQuery,
  useGetFaqItemDefaultsQuery,
  useGetFaqItemDefaultUsagesQuery,
  useGetFaqAuditQuery,
  useRestoreFaqSectionMutation,
  useRestoreFaqItemMutation,
  usePatchFaqItemDefaultMutation,
  useMakeFaqItemDefaultMutation,
  useCreateFaqSectionMutation,
  usePatchFaqSectionMutation,
  useDeleteFaqSectionMutation,
  useCreateFaqItemMutation,
  usePatchFaqItemMutation,
  useDeleteFaqItemMutation,
  useCopyFaqScopeMutation,
  useReorderFaqSectionsMutation,
  useReorderFaqItemsMutation,
} = faqsApi;
